import type { Identifier, RaRecord } from 'react-admin';

import { readProjectSettings } from './adminSettings';
import { readAdminSession } from './authProvider';
import { toApiIsoString } from './dateTimeUtils';

export const ADMIN_USERS_RESOURCE = 'admin-users' as const;

type AdminApiEnvelope<T> = {
    success?: boolean;
    data?: T;
    statusCode?: number;
    errorMessage?: string | null;
    message?: string;
    error?: string;
    title?: string;
    detail?: string;
};

export type AdminUserRecord = RaRecord & {
    id: string;
    userId: string;
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    emailVerified?: boolean | null;
    mobile?: string | null;
    mobileVerified?: boolean | null;
    verified?: boolean | null;
    authBy?: string | null;
    isActivated?: boolean | null;
    isAdmin?: boolean | null;
    isDeleted?: boolean | null;
    isProfileComplete?: boolean | null;
    country?: string | null;
    language?: string | null;
    examName?: string | null;
    createdVia?: string | null;
    adminTags?: string[];
    profileImageUrl?: string | null;
    profileImageUrlPending?: string | null;
    livesIn?: string | null;
    placeId?: string | null;
    lat?: number | null;
    lon?: number | null;
    dateOfBirth?: string | null;
    gender?: string | null;
    state?: string | null;
    postalNumber?: string | null;
    education?: string | null;
    mobileCode?: string | null;
    street?: string | null;
    localArea?: string | null;
    district?: string | null;
    taluka?: string | null;
    cfCountry?: string | null;
    createdAt?: string | null;
    updatedAt?: string | null;
    adminSetPassword?: string | null;
    fullName?: string;
    adminTagsText?: string;
    [key: string]: unknown;
};

function trimTrailingSlash(value: string): string {
    return value.replace(/\/+$/, '');
}

function trimSlashes(value: string): string {
    return value.replace(/^\/+|\/+$/g, '');
}

function hasVersionedApiPath(value: string): boolean {
    return /\/api\/v[^/]+$/i.test(trimTrailingSlash(value));
}

function getAdminApiConfig() {
    const settings = readProjectSettings();
    const session = readAdminSession();
    const baseUrl = trimTrailingSlash(
        String(
            import.meta.env.VITE_API_BASE_URL ??
                import.meta.env.VITE_API_URL ??
                settings.adminApiBaseUrl ??
                'https://test-api.pteultra.com/api/v1',
        ),
    );
    const apiVersion = String(import.meta.env.VITE_API_VERSION ?? settings.adminApiVersion ?? '1');
    const adminToken = String(
        session?.adminToken ?? session?.accessToken ?? import.meta.env.VITE_ADMIN_TOKEN ?? settings.adminApiToken ?? '',
    );

    return { baseUrl, apiVersion, adminToken };
}

function getErrorMessage(payload: unknown, fallback: string): string {
    if (typeof payload === 'string' && payload.trim()) return payload;
    if (payload && typeof payload === 'object') {
        const candidate = payload as Record<string, unknown>;
        const direct =
            candidate.errorMessage ?? candidate.message ?? candidate.error ?? candidate.title ?? candidate.detail;
        if (typeof direct === 'string' && direct.trim()) return direct;
    }
    return fallback;
}

function unwrapAdminApiPayload<T>(payload: T | AdminApiEnvelope<T>): T {
    if (!payload || typeof payload !== 'object' || !('data' in payload)) {
        return payload as T;
    }

    const envelope = payload as AdminApiEnvelope<T>;

    if (envelope.success === false) {
        throw new Error(getErrorMessage(envelope, 'Admin user request failed'));
    }

    if (envelope.data !== undefined) {
        return envelope.data;
    }

    return payload as T;
}

async function requestAdminUsers<T>(path = '', init: RequestInit = {}, query?: Record<string, string>): Promise<T> {
    const { baseUrl, apiVersion, adminToken } = getAdminApiConfig();
    const apiRoot = hasVersionedApiPath(baseUrl) ? baseUrl : `${baseUrl}/api/v${apiVersion}`;
    const requestPath = trimSlashes(path);
    const url = new URL(requestPath ? `${apiRoot}/AdminUser/${requestPath}` : `${apiRoot}/AdminUser`);

    for (const [key, value] of Object.entries(query ?? {})) {
        if (value !== '') {
            url.searchParams.set(key, value);
        }
    }

    const headers = new Headers(init.headers);
    if (!headers.has('Accept')) {
        headers.set('Accept', 'application/json');
    }
    if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
    }
    if (adminToken && !headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${adminToken}`);
    }

    const response = await fetch(url.toString(), {
        ...init,
        headers,
    });

    const contentType = response.headers.get('content-type') ?? '';
    const payload = contentType.includes('application/json') ? await response.json() : await response.text();

    if (!response.ok) {
        throw new Error(getErrorMessage(payload, `${response.status} ${response.statusText}`));
    }

    if (payload && typeof payload === 'object' && 'success' in (payload as Record<string, unknown>)) {
        const envelope = payload as AdminApiEnvelope<unknown>;
        if (envelope.success === false) {
            throw new Error(getErrorMessage(envelope, 'Admin user request failed'));
        }
    }

    return payload as T;
}

function isObject(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === 'object' && !Array.isArray(value);
}

function normalizeString(value: unknown): string | null {
    const text = String(value ?? '').trim();
    return text ? text : null;
}

function normalizeBoolean(value: unknown): boolean | null | undefined {
    if (value === undefined) return undefined;
    if (value === null || value === '') return null;
    if (typeof value === 'boolean') return value;
    const lowered = String(value).trim().toLowerCase();
    if (lowered === 'true') return true;
    if (lowered === 'false') return false;
    return null;
}

function normalizeStringArray(value: unknown): string[] {
    if (Array.isArray(value)) {
        return value.map((item) => String(item ?? '').trim()).filter(Boolean);
    }

    if (typeof value === 'string') {
        return value
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean);
    }

    return [];
}

function normalizeDateTime(value: unknown): string | null {
    const text = normalizeString(value);
    if (!text) return null;

    return toApiIsoString(text);
}

function normalizeAdminUserRecord(item: unknown, fallbackId?: Identifier): AdminUserRecord {
    const record = isObject(item) ? item : {};
    const rawId = record.id ?? record.userId ?? fallbackId;

    if (rawId === undefined || rawId === null || rawId === '') {
        throw new Error('Admin user item is missing userId');
    }

    const firstName = normalizeString(record.firstName);
    const lastName = normalizeString(record.lastName);
    const adminTags = normalizeStringArray(record.adminTags);

    return {
        ...record,
        id: String(rawId),
        userId: String(record.userId ?? rawId),
        firstName,
        lastName,
        email: normalizeString(record.email),
        emailVerified: normalizeBoolean(record.emailVerified),
        mobile: normalizeString(record.mobile),
        mobileVerified: normalizeBoolean(record.mobileVerified),
        verified: normalizeBoolean(record.verified),
        authBy: normalizeString(record.authBy),
        isActivated: normalizeBoolean(record.isActivated),
        isAdmin: normalizeBoolean(record.isAdmin),
        isDeleted: normalizeBoolean(record.isDeleted),
        isProfileComplete: normalizeBoolean(record.isProfileComplete),
        country: normalizeString(record.country),
        language: normalizeString(record.language),
        examName: normalizeString(record.examName),
        createdVia: normalizeString(record.createdVia),
        adminTags,
        profileImageUrl: normalizeString(record.profileImageUrl),
        profileImageUrlPending: normalizeString(record.profileImageUrlPending),
        livesIn: normalizeString(record.livesIn),
        placeId: normalizeString(record.placeId),
        lat: typeof record.lat === 'number' ? record.lat : record.lat == null ? null : Number(record.lat),
        lon: typeof record.lon === 'number' ? record.lon : record.lon == null ? null : Number(record.lon),
        dateOfBirth: normalizeString(record.dateOfBirth),
        gender: normalizeString(record.gender),
        state: normalizeString(record.state),
        postalNumber: normalizeString(record.postalNumber),
        education: normalizeString(record.education),
        mobileCode: normalizeString(record.mobileCode),
        street: normalizeString(record.street),
        localArea: normalizeString(record.localArea),
        district: normalizeString(record.district),
        taluka: normalizeString(record.taluka),
        cfCountry: normalizeString(record.cfCountry),
        createdAt: normalizeString(record.createdAt),
        updatedAt: normalizeString(record.updatedAt),
        fullName: [firstName, lastName].filter(Boolean).join(' ').trim(),
        adminTagsText: adminTags.join(', '),
    };
}

function getListPayloadObject(payload: unknown): Record<string, unknown> | null {
    const unwrapped = unwrapAdminApiPayload(
        payload as Record<string, unknown> | AdminApiEnvelope<Record<string, unknown>>,
    );

    return isObject(unwrapped) ? unwrapped : null;
}

function getListRecords<T>(payload: unknown): T[] {
    const unwrapped = unwrapAdminApiPayload(payload as T[] | AdminApiEnvelope<T[]>);

    if (Array.isArray(unwrapped)) {
        return unwrapped;
    }

    const candidate =
        unwrapped && typeof unwrapped === 'object' && !Array.isArray(unwrapped)
            ? (unwrapped as Record<string, unknown>)
            : null;

    if (candidate) {
        if (Array.isArray(candidate.items)) {
            return candidate.items as T[];
        }
        if (Array.isArray(candidate.data)) {
            return candidate.data as T[];
        }
    }

    return [];
}

function getListTotal(payload: unknown, fallback: number): number {
    const unwrapped = getListPayloadObject(payload);

    if (unwrapped) {
        if (typeof unwrapped.total === 'number') return unwrapped.total;
        if (typeof unwrapped.totalCount === 'number') return unwrapped.totalCount;
        if (typeof unwrapped.count === 'number') return unwrapped.count;
    }

    return fallback;
}

function toQueryValue(value: unknown): string {
    if (value === undefined || value === null) return '';
    return String(value).trim();
}

function normalizeSortField(field: string | undefined): string {
    const candidate = String(field ?? 'createdAt').trim().toLowerCase();

    switch (candidate) {
        case 'createdat':
        case 'updatedat':
        case 'firstname':
        case 'lastname':
        case 'email':
        case 'mobile':
        case 'country':
        case 'examname':
        case 'isadmin':
        case 'isactivated':
        case 'verified':
            return candidate;
        default:
            return 'createdAt';
    }
}

function buildAdminUserPayload(data: Record<string, unknown>) {
    return {
        firstName: normalizeString(data.firstName),
        lastName: normalizeString(data.lastName),
        email: normalizeString(data.email),
        emailVerified: normalizeBoolean(data.emailVerified),
        mobile: normalizeString(data.mobile),
        mobileVerified: normalizeBoolean(data.mobileVerified),
        verified: normalizeBoolean(data.verified),
        language: normalizeString(data.language),
        livesIn: normalizeString(data.livesIn),
        placeId: normalizeString(data.placeId),
        lat: data.lat === '' || data.lat === null || data.lat === undefined ? null : Number(data.lat),
        lon: data.lon === '' || data.lon === null || data.lon === undefined ? null : Number(data.lon),
        profileImageUrl: normalizeString(data.profileImageUrl),
        profileImageUrlPending: normalizeString(data.profileImageUrlPending),
        dateOfBirth: normalizeDateTime(data.dateOfBirth),
        gender: normalizeString(data.gender),
        state: normalizeString(data.state),
        authBy: normalizeString(data.authBy),
        isActivated: normalizeBoolean(data.isActivated),
        country: normalizeString(data.country),
        postalNumber: normalizeString(data.postalNumber),
        education: normalizeString(data.education),
        mobileCode: normalizeString(data.mobileCode),
        isAdmin: normalizeBoolean(data.isAdmin),
        street: normalizeString(data.street),
        localArea: normalizeString(data.localArea),
        district: normalizeString(data.district),
        taluka: normalizeString(data.taluka),
        adminTags: normalizeStringArray(data.adminTags),
        createdVia: normalizeString(data.createdVia),
        isProfileComplete: normalizeBoolean(data.isProfileComplete),
        cfCountry: normalizeString(data.cfCountry),
        examName: normalizeString(data.examName),
        adminSetPassword: normalizeString(data.adminSetPassword),
    };
}

export function isAdminUsersResource(resource: string): resource is typeof ADMIN_USERS_RESOURCE {
    return resource === ADMIN_USERS_RESOURCE;
}

export async function getAdminUsersList(params: any) {
    const filter = (params.filter ?? {}) as Record<string, unknown>;
    const { page = 1, perPage = 25 } = params.pagination ?? {};
    const sortField = normalizeSortField(params.sort?.field);
    const sortOrder = String(params.sort?.order ?? 'DESC').toLowerCase() === 'asc' ? 'asc' : 'desc';
    const query = {
        page: String(page),
        pageSize: String(perPage),
        search: toQueryValue(filter.search ?? filter.q ?? ''),
        sortBy: sortField,
        sortDirection: sortOrder,
        isAdmin: toQueryValue(filter.isAdmin ?? ''),
        isActivated: toQueryValue(filter.isActivated ?? ''),
        isDeleted: toQueryValue(filter.isDeleted ?? ''),
        verified: toQueryValue(filter.verified ?? ''),
        emailVerified: toQueryValue(filter.emailVerified ?? ''),
        mobileVerified: toQueryValue(filter.mobileVerified ?? ''),
        isProfileComplete: toQueryValue(filter.isProfileComplete ?? ''),
        hasProfileImage: toQueryValue(filter.hasProfileImage ?? ''),
        hasPendingProfileImage: toQueryValue(filter.hasPendingProfileImage ?? ''),
        authBy: toQueryValue(filter.authBy ?? ''),
        examName: toQueryValue(filter.examName ?? ''),
        country: toQueryValue(filter.country ?? ''),
        language: toQueryValue(filter.language ?? ''),
        createdVia: toQueryValue(filter.createdVia ?? ''),
        adminTag: toQueryValue(filter.adminTag ?? ''),
        createdFromUtc: toQueryValue(filter.createdFromUtc ?? ''),
        createdToUtc: toQueryValue(filter.createdToUtc ?? ''),
        updatedFromUtc: toQueryValue(filter.updatedFromUtc ?? ''),
        updatedToUtc: toQueryValue(filter.updatedToUtc ?? ''),
    };

    const payload = await requestAdminUsers<unknown>('', { method: 'GET' }, query);
    const records = getListRecords<unknown>(payload).map((item) => normalizeAdminUserRecord(item));

    return {
        data: records,
        total: getListTotal(payload, records.length),
    };
}

export async function getAdminUsersOne(params: any) {
    const payload = unwrapAdminApiPayload(
        await requestAdminUsers<unknown | AdminApiEnvelope<unknown>>(String(params.id), {
            method: 'GET',
        }),
    );

    return { data: normalizeAdminUserRecord(payload, params.id) };
}

export async function getManyAdminUsers(params: any) {
    const data = await Promise.all(
        params.ids.map(async (id: Identifier) => {
            const payload = unwrapAdminApiPayload(
                await requestAdminUsers<unknown | AdminApiEnvelope<unknown>>(String(id), {
                    method: 'GET',
                }),
            );

            return normalizeAdminUserRecord(payload, id);
        }),
    );

    return { data };
}

export async function createAdminUser(params: any) {
    const payload = unwrapAdminApiPayload(
        await requestAdminUsers<unknown | AdminApiEnvelope<unknown>>('', {
            method: 'POST',
            body: JSON.stringify(buildAdminUserPayload((params.data ?? {}) as Record<string, unknown>)),
        }),
    );

    return { data: normalizeAdminUserRecord(payload) };
}

export async function updateAdminUser(params: any) {
    const payload = unwrapAdminApiPayload(
        await requestAdminUsers<unknown | AdminApiEnvelope<unknown>>(String(params.id), {
            method: 'PUT',
            body: JSON.stringify(buildAdminUserPayload((params.data ?? {}) as Record<string, unknown>)),
        }),
    );

    return { data: normalizeAdminUserRecord(payload, params.id) };
}

export async function deleteAdminUser(params: any) {
    const existing = params.previousData
        ? normalizeAdminUserRecord(params.previousData, params.id)
        : (await getAdminUsersOne(params)).data;

    await requestAdminUsers<unknown | AdminApiEnvelope<unknown>>(String(params.id), {
        method: 'DELETE',
    });

    return { data: existing };
}

export async function restoreAdminUser(userId: Identifier) {
    await requestAdminUsers<unknown | AdminApiEnvelope<unknown>>(`restore/${String(userId)}`, {
        method: 'POST',
    });

    return getAdminUsersOne({ id: userId });
}